import {
  useContext,
  createContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  setPersistence,
  User,
} from "firebase/auth";
import { useRouter } from "next/navigation";
import { provider } from "@/lib/auth/googleAuthProvider";
import {
  fireSignInAbandonEvent,
  fireSignInEvent,
  fireSignOutEvent,
  identify,
  resetTrackingId,
  handleLoginPromptOpenEvent,
} from "@/lib/analytics/authentication";

type AuthContextProviderProps = {
  children: ReactNode;
};

export type AuthContextState = {
  user: User | null;
  login: () => Promise<void>;
  logOut: () => Promise<void>;
  showLoginPrompt: boolean;
  openLoginPrompt: (target: string) => void;
  closeLoginPrompt: () => void;
};

export const AuthContext = createContext<AuthContextState>({
  user: null,
  login: async () => undefined,
  logOut: async () => undefined,
  showLoginPrompt: false,
  openLoginPrompt: () => undefined,
  closeLoginPrompt: () => undefined,
});

const AuthProvider = ({ children }: AuthContextProviderProps) => {
  const auth = getAuth();
  const { push } = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [authInitializing, setAuthInitializing] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence);
  }, [auth]);

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthInitializing(false);
    });
  }, [auth]);

  const login = async () => {
    try {
      fireSignInEvent();
      const { user } = await signInWithPopup(auth, provider);
      identify(user.email!);
    } catch (error: any) {
      if (error.code === "auth/popup-closed-by-user") {
        fireSignInAbandonEvent();
      } else {
        console.error("Error logging in:", error);
      }
    }
  };

  const logOut = async () => {
    try {
      fireSignOutEvent();
      await auth.signOut();
      resetTrackingId();
      push("/home");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const openLoginPrompt = (target: string) => {
    handleLoginPromptOpenEvent(target);
    setShowLoginPrompt(true);
  };
  const closeLoginPrompt = () => {
    setShowLoginPrompt(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logOut,
        showLoginPrompt,
        openLoginPrompt,
        closeLoginPrompt,
      }}
    >
      {!authInitializing && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
